import { useCallback, useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import type { OrcamentoResponse } from '@triade/contracts';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { usePode } from '@/api/me';
import { useOrcamentos, useResumoOrcamentos } from '@/api/orcamentos';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { BarraDeAcoesLista, TAMANHOS_PAGINA } from '@/components/barra-lista';
import { BotaoExportar, ErroExportacao, useExportar } from '@/components/exportar';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { formatarContagem } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { BotaoRelatorios } from '@/relatorios';
import { AlternarVisao } from './alternar-visao';
import { CartoesResumo } from './cartoes-resumo';
import { FiltrosLista } from './filtros-lista';
import { LegendaAlertaCusto } from './alerta-custo';
import { OrcamentosTabela } from './orcamentos-tabela';
import { paraConsulta, paraExportacao, paraRelatorio, paraResumo, TAMANHO_PAGINA, temFiltro, type BuscaLista, type Ordenar } from './tipos';

/** Tela "Orçamentos — lista" (/vender/orcamentos). Filtros, ordenacao, paginacao, total e cartoes vem da API. */
export function OrcamentosListaPage({ busca }: { busca: BuscaLista }) {
  const { empresaId } = useSessao();
  const navigate = useNavigate();
  const podeEditar = usePode().pode('orcamento.editar');
  const ordenar = busca.ordenar ?? 'numero';
  const direcao = busca.direcao ?? 'desc';
  const pagina = busca.pagina ?? 1;

  const { exportando, erro: erroExportar, exportar } = useExportar();
  const lista = useOrcamentos(paraConsulta(busca), { manter: true });
  const resumo = useResumoOrcamentos(paraResumo(busca));
  const tabelas = useTabelasPreco({ limit: 200 });
  const condicoes = useCondicoesPagamento({ limit: 200 });

  const mudar = useCallback(
    (mudanca: Partial<BuscaLista>) => {
      void navigate({ to: '/vender/orcamentos', search: (anterior: BuscaLista) => ({ ...anterior, ...mudanca }), replace: true });
    },
    [navigate],
  );
  const nomeTabela = useCallback((id: string | null) => (id ? tabelas.data?.find((t) => t.id === id)?.nome : undefined), [tabelas.data]);
  const nomeCondicao = useCallback((id: string) => condicoes.data?.find((c) => c.id === id)?.nome, [condicoes.data]);
  const ordenarPor = useCallback(
    (campo: Ordenar) => mudar({ ordenar: campo, direcao: campo === ordenar ? (direcao === 'asc' ? 'desc' : 'asc') : campo === 'cliente' ? 'asc' : 'desc', pagina: undefined }),
    [mudar, ordenar, direcao],
  );
  const abrir = useCallback((o: OrcamentoResponse) => void navigate({ to: '/vender/orcamentos/$id', params: { id: o.id } }), [navigate]);

  const total = lista.data?.total ?? 0;
  const itens = lista.data?.itens ?? [];
  const tamanho = busca.tamanho ?? TAMANHO_PAGINA;
  const de = total === 0 ? 0 : (pagina - 1) * tamanho + 1;
  const ate = (pagina - 1) * tamanho + itens.length;

  let corpo;
  if (empresaId === null) corpo = <Mensagem>Escolha a empresa no menu lateral para ver os orçamentos.</Mensagem>;
  else if (lista.isError && !lista.data) corpo = <div className="p-4"><ErroFormacao erro={lista.error} onTentar={() => void lista.refetch()} /></div>;
  else if (!lista.data) corpo = <Mensagem papel="status">Carregando orçamentos…</Mensagem>;
  else if (itens.length === 0) {
    corpo = temFiltro(busca) ? (
      <Mensagem papel="status">
        Nenhum orçamento com esses filtros.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => void navigate({ to: '/vender/orcamentos', search: {}, replace: true })}>
          Limpar filtros
        </button>
      </Mensagem>
    ) : (
      <Mensagem papel="status">
        Nenhum orçamento ainda.
        {podeEditar && (
          <>
            {' '}
            <Link to="/vender/orcamentos/novo" className="font-medium text-primary hover:underline">
              Crie o primeiro
            </Link>
            .
          </>
        )}
      </Mensagem>
    );
  } else {
    corpo = (
      <div className="flex flex-col gap-3">
        <OrcamentosTabela orcamentos={itens} ordenar={ordenar} direcao={direcao} onOrdenar={ordenarPor} nomeTabela={nomeTabela} nomeCondicao={nomeCondicao} onAbrir={abrir} />
        <LegendaAlertaCusto />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Vender <span aria-hidden="true">/</span> Orçamentos
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Orçamentos</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AlternarVisao atual="lista" busca={busca} />
          <BotaoExportar rotulo="Exportar Excel" exportando={exportando} desabilitado={empresaId === null} onClick={() => void exportar('/orcamentos.xlsx', paraExportacao(busca))} />
        </div>
      </div>

      <ErroExportacao erro={erroExportar} />

      <CartoesResumo
        resumo={resumo.data}
        carregando={resumo.isFetching}
        erro={resumo.isError && !resumo.data ? resumo.error.message : null}
        onTentar={() => void resumo.refetch()}
        abaixoAtivo={Boolean(busca.abaixo)}
        onAbaixo={(ligado) => mudar({ abaixo: ligado || undefined, pagina: undefined })}
      />

      <section aria-label="Lista de orçamentos" aria-busy={lista.isFetching} className="rounded-2xl border border-border bg-card">
        <FiltrosLista busca={busca} onBusca={mudar} resumo={resumo.data} />
        {corpo}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-small text-ink-secondary">
          <span data-testid="contagem-lista">
            {lista.data ? `Mostrando ${formatarContagem(de)}–${formatarContagem(ate)} de ${formatarContagem(total)}` : ' '}
          </span>
        </div>
      </section>

      <BarraDeAcoesLista
        novo={podeEditar ? { rotulo: 'Novo orçamento', onClick: () => void navigate({ to: '/vender/orcamentos/novo' }) } : undefined}
        paginacao={{
          pagina,
          tamanho,
          total,
          onPagina: (p) => mudar({ pagina: p > 1 ? p : undefined }),
          onTamanho: (t) => mudar({ tamanho: t === TAMANHO_PAGINA ? undefined : t, pagina: undefined }),
          tamanhos: [...TAMANHOS_PAGINA],
        }}
        relatorios={<BotaoRelatorios tela="orcamentos-lista" params={paraRelatorio(busca)} />}
      />
    </div>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
